import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import countries from "utils/static/countries.json";
import states from "utils/static/states.json";
import doNotRequirePostalCode from "utils/static/doNotRequireZipCode.json";
import AddressForm from "components/ModalCheckout134/addressForm";
import Autocomplete from "react-google-autocomplete";
import { usePlacesWidget } from "react-google-autocomplete";
import { motion } from "framer-motion";
import Input from "./Input";
import { SlideInFromLeft } from "./animations/slideInFromLeft";
import { useTracking } from "components/trackEvent";

export type AddressForm = {
  streetNo?: string;
  addressLine1: string;
  addressLine2?: string;
  country: string;
  stateProvince?: string;
  city: string;
  postalCode?: string;
  phone?: string;
  validated?: boolean;
};

const Address = ({
  onNext,
  shippingRequired,
}: {
  onNext: () => void;
  shippingRequired: boolean;
}) => {
  const {
    register,
    watch,
    handleSubmit,
    formState: { errors, dirtyFields },
    reset,
    clearErrors,
    getValues,
    setValue,
  } = useFormContext<{ address: AddressForm }>();
  const [manuallyEnterAddress, setManuallyEnterAddress] = useState(false);
  const [hasSubmittedAutoComplete, setHasSubmittedAutoComplete] =
    useState(false);

  const { ref, autocompleteRef } = usePlacesWidget<HTMLInputElement>({
    apiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY,
    onPlaceSelected: (place) => {
      setHasSubmittedAutoComplete(true);
      handlePlaceSelected(place);

      // TODO: track address autocomplete
      // probably want to track user manually entering address as well
      // track({
      //   action: "address_autocomplete",
      // });

      setTimeout(() => {
        verifyAddress();
      });
      setManuallyEnterAddress(false);
    },
    options: {
      types: ["address"],
    },
  });

  const [needsStreetNo, setNeedsStreetNo] = useState(false);
  const [postalCodeRequired, setPostalCodeRequired] = useState(true);
  const [stateProviceRequired, setStateProvinceRequired] = useState(false);
  const [internationalShippingRequired, setInternationalShippingRequired] =
    useState(false);

  const country = watch("address.country");
  const city = watch("address.city");
  const addressLine1 = watch("address.addressLine1");
  const postalCode = watch("address.postalCode");
  const stateProvince = watch("address.stateProvince");

  useEffect(() => {
    // show the manual address entry if there are errors in the address fields
    // that the user will need to address
    const errorInAddress = !!Object.keys(errors?.address || {}).find((key) => {
      return ["city", "addressLine1", "country", "postalCode"].includes(key);
    });

    if (errorInAddress && hasSubmittedAutoComplete) {
      setManuallyEnterAddress(true);
    }
  }, [country, city, addressLine1, postalCode]);

  useEffect(() => {
    if (
      country &&
      city &&
      (addressLine1?.length || 0) > 2 &&
      (postalCode?.length || 0) > 2 &&
      (checkIfNeedsStateProvince() ? stateProvince : true)
    ) {
      verifyAddress();
    }
  }, [country, city, addressLine1, postalCode, stateProvince]);

  useEffect(() => {
    setPostalCodeRequired(checkIfPostalCodeIsRequired());
    setStateProvinceRequired(checkIfNeedsStateProvince());
    setNeedsStreetNo(checkIfNeedsStreetNo());
    setInternationalShippingRequired(!!country && country !== "US");
    clearErrors();
  }, [country]);

  const checkIfPostalCodeIsRequired = () => {
    return !doNotRequirePostalCode.includes(country);
  };

  const checkIfNeedsStateProvince = () => {
    const needsStateProvince = ["AU", "CA", "US", "IE"];
    return needsStateProvince.includes(country);
  };

  const checkIfNeedsStreetNo = (c?: string) => {
    const needsStreetNo: string[] = ["AT", "DE", "CH"];
    return needsStreetNo.includes(c || country);
  };

  const verifyAddress = async () => {
    // TODO: Verify address on server - we don't do anything with this on the client and it makes the code more confusing

    const address = getValues("address");

    const verifyShippingAddress = await fetch("/api/shipping/verifyAddress/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        address: address,
      }),
    });

    const responseJson = await verifyShippingAddress.json();

    if (responseJson.address.validation_results.is_valid) {
      setValue("address", {
        country: address.country,
        phone: address.phone,
        stateProvince: address.stateProvince,
        streetNo: responseJson.address.street_no,
        addressLine1: responseJson.address.street1,
        addressLine2: responseJson.address.street2,
        city: responseJson.address.city,
        postalCode: responseJson.address.zip,
        validated: true,
      });
    } else {
      setValue("address", {
        ...address,
        validated: false,
      });
    }
  };

  const handlePlaceSelected = (place: any) => {
    if (!place?.address_components) {
      return;
    }
    const address = place.address_components;
    const streetNumber = address.find((a: any) =>
      a.types.includes("street_number")
    );
    const streetName = address.find((a: any) => a.types.includes("route"));
    const city = address.find((a: any) => a.types.includes("locality"));
    const state = address.find((a: any) =>
      a.types.includes("administrative_area_level_1")
    );
    const country = address.find((a: any) => a.types.includes("country"));
    const postalCode = address.find((a: any) =>
      a.types.includes("postal_code")
    );

    const streetNo = streetNumber?.long_name;
    const addressLine1 = streetName?.long_name;
    const cityValue = city?.long_name;
    const stateProvince = state?.short_name;
    const countryValue = country?.short_name;
    const postalCodeValue = postalCode?.long_name;

    let addressLine1Computed = [addressLine1];
    const needsStreetNo = checkIfNeedsStreetNo(countryValue);
    // some countries need streetNo as its own field,
    // most can just have it as part of addressLine1
    if (!needsStreetNo) {
      addressLine1Computed = [streetNo, addressLine1];
    }

    const addressForm: AddressForm = {
      streetNo: needsStreetNo ? streetNo : "",
      addressLine1: addressLine1Computed.join(" "),
      addressLine2: "",
      country: countryValue,
      stateProvince: stateProvince,
      city: cityValue,
      postalCode: postalCodeValue,
      phone: "",
    };

    setValue("address", addressForm, { shouldValidate: true });
  };

  return (
    <>
      <Input
        ref={ref}
        errors={errors}
        label="Address"
        placeholder="Start typing your address"
        name="address"
        register={() => {}}
        testId="address-autocomplete"
      />

      <span
        className="text-blue-500 underline text-right hover:text-blue-600 cursor-pointer"
        onClick={() => {
          setManuallyEnterAddress(true);
          ref.current && ref.current.value && (ref.current.value = "");
        }}
      >
        Manually enter address
      </span>
      <SlideInFromLeft show={manuallyEnterAddress}>
        {needsStreetNo && (
          <Input
            name="address.streetNo"
            label="Street Number"
            errors={errors}
            register={register}
            rules={{ required: false }}
          />
        )}
        <Input
          name="address.name"
          label="Name"
          errors={errors}
          register={register}
        />
        <Input
          name="address.addressLine1"
          label="Address Line 1"
          errors={errors}
          register={register}
          rules={{
            required: shippingRequired ? "Address Line 1 is required" : false,
          }}
        />
        <Input
          name="address.addressLine2"
          label="Address Line 2 (optional)"
          errors={errors}
          register={register}
        />
        <Input
          errors={errors}
          label="Country"
          name="address.country"
          options={countries}
          register={register}
          rules={{ required: shippingRequired ? "Country is required" : false }}
          type="select"
        />
        {stateProviceRequired && (
          <>
            {internationalShippingRequired ? (
              <Input
                name="address.stateProvince"
                label="State"
                errors={errors}
                register={register}
              />
            ) : (
              <Input
                name="address.stateProvince"
                label="State"
                type="select"
                options={states}
                errors={errors}
                register={register}
                rules={{
                  required: shippingRequired ? "State is required" : false,
                }}
              />
            )}
          </>
        )}
        <Input
          name="address.city"
          label="City"
          errors={errors}
          register={register}
          rules={{ required: shippingRequired ? "City is required" : false }}
        />
        <Input
          name="address.postalCode"
          label="Postal Code"
          errors={errors}
          register={register}
          rules={{
            required:
              shippingRequired && postalCodeRequired
                ? "Postal code is required"
                : false,
          }}
        />
      </SlideInFromLeft>
      <SlideInFromLeft
        show={internationalShippingRequired || manuallyEnterAddress}
      >
        <Input
          name="address.phone"
          label={
            internationalShippingRequired
              ? "Phone Number"
              : "Phone Number (optional)"
          }
          type="tel"
          errors={errors}
          register={register}
          rules={{
            required:
              shippingRequired && internationalShippingRequired
                ? "Phone is required"
                : false,
            minLength: internationalShippingRequired
              ? {
                  value: 8,
                  message:
                    "Must be at least 8 digits (please include country code)",
                }
              : 0,
          }}
        />
      </SlideInFromLeft>
    </>
  );
};

export default Address;
